import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeState {
  mode: ThemeMode;
  isDark: boolean;
  setMode: (mode: ThemeMode) => void;
  toggle: () => void;
}

function getSystemDark(): boolean {
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function applyDark(dark: boolean): void {
  if (dark) {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
}

function resolveDark(mode: ThemeMode): boolean {
  if (mode === 'system') return getSystemDark();
  return mode === 'dark';
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      mode: 'system',
      isDark: getSystemDark(),
      setMode: (mode: ThemeMode) => {
        const dark = resolveDark(mode);
        applyDark(dark);
        set({ mode, isDark: dark });
      },
      toggle: () => {
        const { mode } = get();
        // cycle: system -> dark -> light -> system
        const next: ThemeMode =
          mode === 'system' ? 'dark' : mode === 'dark' ? 'light' : 'system';
        const dark = resolveDark(next);
        applyDark(dark);
        set({ mode: next, isDark: dark });
      },
    }),
    {
      name: 'kassenzettel-theme',
      onRehydrateStorage: () => (state) => {
        if (!state) return;
        const dark = resolveDark(state.mode);
        applyDark(dark);
        // Keep isDark in sync after rehydration
        state.isDark = dark;
      },
    }
  )
);

// Listen for OS-level dark mode changes when mode is 'system'
if (typeof window !== 'undefined') {
  window
    .matchMedia('(prefers-color-scheme: dark)')
    .addEventListener('change', (e) => {
      const { mode } = useThemeStore.getState();
      if (mode === 'system') {
        applyDark(e.matches);
        useThemeStore.setState({ isDark: e.matches });
      }
    });
}
